import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Checkbox, CheckboxSize, CheckboxType } from '@nx-frontend/shared/ui/checkbox';
import { UiInput, UiInputView } from '@nx-frontend/shared/ui/input';
import { $ModalStore, closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { useLinkCreate } from '@nx-frontend/shared/hooks';
import { CreatePortalLinkPayload, PopupType } from '@nx-frontend/shared/types';

export function CreatePortalLinkPopup() {
  const t = useTranslations('customizer');
  const [modalStore] = useUnit([$ModalStore]);
  const [closeModal] = useEvent([closeModalEvent]);
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [linkError, setLinkError] = useState(false);
  const [openNewTab, setOpenNewTab] = useState(true);
  const [visible, setVisible] = useState(true);
  const createLink = useLinkCreate();

  const modal = modalStore.find((m) => m.content === PopupType.CREATE_PORTAL_LINK);
  const payload = modal?.payload as CreatePortalLinkPayload;

  const handleLinkChange = (url: string) => {
    setLinkError(false);

    const dotIndex = url.lastIndexOf('.');
    if ((url.includes('.') && dotIndex === -1) || (url.includes('.') && dotIndex === url.length - 1)) {
      setLinkError(true);
      return;
    }

    if (url && !url.match(/^https?:\/\//i) && url.includes('.')) {
      url = 'https://' + url;
    }

    setLink(url);
  };

  const submit = async () => {
    await createLink(
      { title, url: link, openInNewTab: openNewTab, visible, openAfterCreate: true },
      { parentId: payload.parentId, menuType: payload.menuType }
    );
    closeModal();
  };

  const submitDisabled = linkError || !title.trim() || !link.trim();

  return (
    <div className="flex flex-col gap-5 mt-5">
      <div className="flex flex-col gap-4 min-w-96">
        <UiInput
          label={t('customizer-input-link-title_label')}
          view={UiInputView.LABEL_TOP}
          value={title}
          autoFocus={true}
          placeholder="Type name here"
          onChange={({ target: { value } }) => setTitle(value)}
        />

        <UiInput
          label={t('customizer-input-link-url_label')}
          view={UiInputView.LABEL_TOP}
          value={link}
          placeholder="https://"
          onChange={({ target: { value } }) => handleLinkChange(value)}
        />

        <Checkbox
          id="open_new_tab"
          checked={openNewTab ?? false}
          onChange={(value) => {
            setOpenNewTab(value);
          }}
          label={t('customizer-open_in_new_tab_option')}
          tooltip={t('customizer-open_in_new_tab_option')}
          type={CheckboxType.SWITCH}
          boxSize={CheckboxSize.SM}
        />

        <Checkbox
          id="visibility"
          checked={visible ?? false}
          onChange={() => setVisible(!visible)}
          label={t('customizer-visibility')}
          type={CheckboxType.SWITCH}
          boxSize={CheckboxSize.SM}
        />
      </div>

      <div className="flex gap-5 justify-end">
        <Button type={ButtonType.STROKED} size={ButtonSize.XL} onClick={() => closeModal()}>
          Cancel
        </Button>
        <Button size={ButtonSize.XL} type={ButtonType.FLAT} onClick={submit} disabled={submitDisabled}>
          Add
        </Button>
      </div>
    </div>
  );
}
